'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Amenity {
  icon: string;
  label: string;
}

interface ApartmentCardProps {
  id: string;
  name: string;
  tagline: string;
  description: string;
  image: string;
  alt: string;
  size: string;
  bedrooms: string;
  bathrooms: string;
  maxOccupancy: string;
  priceFrom: string;
  amenities: Amenity[];
  features: string[];
  onViewDetails: (id: string) => void;
  onBookNow: (id: string) => void;
}

export default function ApartmentCard({
  id,
  name,
  tagline,
  description,
  image,
  alt,
  size,
  bedrooms,
  bathrooms,
  maxOccupancy,
  priceFrom,
  amenities,
  features,
  onViewDetails,
  onBookNow,
}: ApartmentCardProps) {
  const [imageError, setImageError] = useState(false);

  return (
    <div className="bg-card rounded-2xl shadow-subtle hover:shadow-elevated transition-all duration-500 overflow-hidden border border-border group">
      <div className="relative h-80 overflow-hidden">
        <AppImage
          src={image}
          alt={alt}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        <div className="absolute top-4 right-4 bg-accent text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
          ₹{priceFrom}/night
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </div>

      <div className="p-8">
        <div className="mb-4">
          <h3 className="font-display text-3xl font-bold text-primary mb-2">{name}</h3>
          <p className="text-accent font-semibold text-lg">{tagline}</p>
        </div>

        <p className="text-text-secondary leading-relaxed mb-6">{description}</p>

        <div className="grid grid-cols-2 gap-4 mb-6 pb-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
              <Icon name="HomeIcon" size={20} className="text-primary" />
            </div>
            <div>
              <p className="text-xs text-text-secondary">Size</p>
              <p className="font-semibold text-text-primary">{size}</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
              <Icon name="UserGroupIcon" size={20} className="text-primary" />
            </div>
            <div>
              <p className="text-xs text-text-secondary">Occupancy</p>
              <p className="font-semibold text-text-primary">{maxOccupancy}</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
              <Icon name="Square3Stack3DIcon" size={20} className="text-primary" />
            </div>
            <div>
              <p className="text-xs text-text-secondary">Bedrooms</p>
              <p className="font-semibold text-text-primary">{bedrooms}</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
              <Icon name="SparklesIcon" size={20} className="text-primary" />
            </div>
            <div>
              <p className="text-xs text-text-secondary">Bathrooms</p>
              <p className="font-semibold text-text-primary">{bathrooms}</p>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <h4 className="font-semibold text-text-primary mb-3 flex items-center">
            <Icon name="CheckBadgeIcon" size={20} className="text-accent mr-2" />
            Key Amenities
          </h4>
          <div className="grid grid-cols-2 gap-3">
            {amenities.slice(0, 6).map((amenity, index) => (
              <div key={index} className="flex items-center space-x-2">
                <Icon name={amenity.icon as any} size={16} className="text-primary flex-shrink-0" />
                <span className="text-sm text-text-secondary">{amenity.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex space-x-3">
          <button
            onClick={() => onViewDetails(id)}
            className="flex-1 px-6 py-3 border-2 border-primary text-primary font-semibold rounded-lg hover:bg-primary hover:text-white transition-all duration-300"
          >
            View Details
          </button>
          <button
            onClick={() => onBookNow(id)}
            className="flex-1 px-6 py-3 bg-accent text-white font-semibold rounded-lg hover:bg-accent/90 shadow-subtle hover:shadow-elevated transition-all duration-300"
          >
            Book Now
          </button>
        </div>
      </div>
    </div>
  );
}